import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-colors cursor-pointer disabled:cursor-default disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-primary/90',
                destructive:
                    'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
                outline:
                    'border border-input bg-background hover:border-primary/50 hover:bg-accent/10 hover:text-foreground',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/90',
                ghost: 'hover:bg-accent/10 hover:text-foreground',
                sidebarGhost:
                    'rounded-lg text-sidebar-foreground/80 hover:bg-transparent hover:text-sidebar-foreground',
                accountGhost:
                    'rounded-none text-[16px] font-normal text-white/88 outline-none transition-colors hover:text-white',
                selector:
                    'border border-input bg-background hover:border-primary/50 hover:bg-accent/10 hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
                stepper: 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-950',
                accountAvatar: 'rounded-full border border-zinc-200 bg-zinc-100 text-zinc-500 hover:text-zinc-950',
                link: 'text-primary underline-offset-4 hover:underline',
                quiet: 'px-0 text-muted-foreground hover:text-foreground',
                quietLink: 'px-0 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline'
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3',
                sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
                lg: 'h-10 px-6 has-[>svg]:px-4',
                icon: 'size-9',
                'icon-sm': 'size-8',
                compact: 'h-auto px-2 py-1',
                compactLg: 'h-10 px-4',
                mobileMenu: 'h-9 w-auto gap-2 px-3',
                account: 'h-auto px-2 py-2',
                selector: 'h-9 gap-2 rounded-md px-2.5 py-1.5',
                stepper: 'h-9 w-9 rounded-none px-0'
            },
            edge: {
                default: '',
                leading: 'border-r border-zinc-200',
                trailing: 'border-l border-zinc-200'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, edge, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return <Comp className={cn(buttonVariants({ variant, size, edge, className }))} ref={ref} {...props} />;
    }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
export type { ButtonProps };
